<script setup lang="ts">
import { useI18n } from '@/plugins/i18n'
import { useSearchStore } from '../store/index.ts'
import StandardsSearchBar from './Standards/index.ce.vue';
import CollectionsSearchBar from './Collections/index.ce.vue';
import UpdatesSearchBar from './Updates/index.ce.vue';
import SearchTabs from './SearchTabs.ce.vue';
import SearchResult from '../SearchResults/index.ce.vue';
import RecentlyViewed from './Common/RecentlyViewed/index.ce.vue';
import { onMounted } from 'vue'
import { STANDARDS_SEARCH_TAB, COLLECTIONS_SEARCH_TAB, UPDATES_SEARCH_TAB, SEARCH_IN_OPTIONS, STANDARDS_SEARCH_SORT_OPTIONS, STANDARDS_SEARCH_SORT_DEFAULT } from '@/components/Search/constants.ts';
import { getStatusDefaultOption } from '../store/statusFacetHelpers.ts';
import { getYearDateRange } from '@/utils/dateHelpers.ts';

//store
const searchStore = useSearchStore()
const { getItem } = searchStore

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const searchBasedOnContext = async (searchbarVars: any) => {
  const segments =  window.location.pathname.split('/').filter(Boolean);
  //starts with #
  const isHashSearch = !!searchbarVars?.hashSearch?.startsWith('#');
  const isSearchPageHasContext = segments.length >= 6 && segments[1] === 'search' && segments[2] === 'index';
  const setSearchBarDefault = !isHashSearch && !isSearchPageHasContext;
  setSubSearchFilterDefaults(searchbarVars, setSearchBarDefault);
  if(isSearchPageHasContext) {
    const searchContext = segments[5];
    const parseJSON = (v: string | null) => {
      try { return v ? JSON.parse(v) : null; }
      catch { return null; }
    };
    let searchParams = null;
    if (searchbarVars?.savedSearchContext === searchContext) {
      searchParams = parseJSON(searchbarVars?.savedSearchParam);
    } else {
      searchParams = parseJSON(localStorage.getItem(searchContext));
    } 
    if (searchParams) {
      searchStore.searchStandardsWithParams(searchParams, false);
      searchStore.activeTab = STANDARDS_SEARCH_TAB;
      searchStore.searchResultActive = true;
      searchStore.displaySearchBar = true;
    }
  } else if(isHashSearch) {
    //perform hash search
    searchStore['standards.documentNumber'] = searchbarVars.hashSearch;
    searchStore.searchStandards();
    searchStore.activeTab = STANDARDS_SEARCH_TAB;
    searchStore.searchResultActive = true;
    searchStore.displaySearchBar = true;
  }
}

const setSubSearchFilterDefaults = (searchbarVars: any, setSearchBarDefault: boolean) => {
  const rawDefaults = searchbarVars?.searchFilterDefaults;
  
  // Normalize defaults to an object (avoid [] / null / string, etc.)
  const searchFilterDefaults: Record<string, any> =
    rawDefaults && typeof rawDefaults === "object" && !Array.isArray(rawDefaults)
      ? rawDefaults
      : {};
  searchStore["filterDefaults"] = searchFilterDefaults;

  // Convert to string and default to "0"
  const rawSearchIn = String(searchFilterDefaults.searchin ?? "0");

  // Validate against allowed ids
  const selectedOption =
    SEARCH_IN_OPTIONS.find(opt => opt.id === rawSearchIn) ??
    SEARCH_IN_OPTIONS.find(opt => opt.id === "0") ??
    SEARCH_IN_OPTIONS[0];

  // Convert to string and default to "0"
  const rawSortDefault = String(searchFilterDefaults.sortby ?? STANDARDS_SEARCH_SORT_DEFAULT.id);

  // Validate against allowed ids
  const selectedSortOption =
    STANDARDS_SEARCH_SORT_OPTIONS.find(opt => opt.id === rawSortDefault) ??
    STANDARDS_SEARCH_SORT_OPTIONS.find(opt => opt.id === STANDARDS_SEARCH_SORT_DEFAULT.id) ??
    STANDARDS_SEARCH_SORT_DEFAULT;
  const selectedStatusOption = getStatusDefaultOption(searchFilterDefaults.status);

  
  if (setSearchBarDefault) {
    searchStore[`${STANDARDS_SEARCH_TAB}.searchIn`] = [selectedOption];
    searchStore[`${STANDARDS_SEARCH_TAB}.result.sort`] = [selectedSortOption];
    searchStore[`${STANDARDS_SEARCH_TAB}.status`] = selectedStatusOption ? [selectedStatusOption] : [];
    searchStore[`${COLLECTIONS_SEARCH_TAB}.status`] = selectedStatusOption ? [selectedStatusOption] : [];
  }
};


//lifecycle hooks
// Get any initial filter values from the server-rendered global variable
const search_bar_vars = (window as any).search_bar_vars || "";
onMounted(async () => {
  //initialize the filter options from stellar api
  searchStore.initStandardSearchBarFilterOptions();
  searchStore.initCollectionSearchBarFilterOptions();

  const searchTabMap = {
    'standards': STANDARDS_SEARCH_TAB,
    'assets': COLLECTIONS_SEARCH_TAB,
    'updates': UPDATES_SEARCH_TAB
  };

  //initialiize the search bar filters from i2i server-rendered global variable
  if (typeof (search_bar_vars) != "undefined" && search_bar_vars !== '') {
    const searchbarVars = JSON.parse(atob(search_bar_vars)); 
    //console.log("searchbarVars", searchbarVars);  
    //init collections search bar vars
    searchStore['collections.documentNumber'] = searchbarVars['collections.documentNumber'] || '';
    searchStore['displaySearchBar'] = searchbarVars.displaySearch || false;
    searchStore['activeCollectionId'] = searchbarVars.activeCollectionId || '';
    searchStore['activeCollectionName'] = searchbarVars.activeCollectionName || '';
    searchStore['nonce'] = searchbarVars.nonce || '';
    searchStore['recentlyViewedDocs'] = searchbarVars.recentlyViewedDocs || [];

    //initialize lookup options
    searchStore['lookUpOptions.collections.documentType'] = searchbarVars.lookUpOptions?.['collections.documentType'] || [];
    searchStore['lookUpOptions.updates.what'] = searchbarVars.lookUpOptions?.['latest.what'] || [];
    searchStore['lookUpOptions.updates.when'] = searchbarVars.lookUpOptions?.['latest.when'] || [];
    searchStore['lookUpOptions.allDescriptorField'] = searchbarVars.lookUpOptions?.['ics.descriptorField'] || [];
    searchStore['lookUpOptions.allDescriptorGroup'] = searchbarVars.lookUpOptions?.['ics.descriptorGroup'] || [];
    searchStore['lookUpOptions.allDescriptorSubGroup'] = searchbarVars.lookUpOptions?.['ics.descriptorSubGroup'] || [];
    searchStore['lookUpOptions.standards.descriptorField'] = searchbarVars.lookUpOptions?.['ics.descriptorField'] || [];
    searchStore['lookUpOptions.collections.descriptorField'] = searchbarVars.lookUpOptions?.['ics.descriptorField'] || [];
    searchStore['lookUpOptions.standards.facetFilter.descriptorField'] = searchbarVars.lookUpOptions?.['ics.descriptorField'] || [];
    searchStore['lookUpOptions.collections.facetFilter.descriptorField'] = searchbarVars.lookUpOptions?.['ics.descriptorField'] || [];

    //set default date range for updates search
    const { start, end } = getYearDateRange();
    searchStore['updates.dateStart'] = start;
    searchStore['updates.dateEnd'] = end;

    //set the standards saved searches
    searchStore['standards.savedSearches'] = searchbarVars['standardsSavedSearches'] || [];
  
    //if there is preloaded search result
    let searchResultData =  (window as any).search_results_data || "";
    let searchType: string = searchTabMap[searchbarVars['activeSearchTab'] as keyof typeof searchTabMap] || STANDARDS_SEARCH_TAB;
    if (searchType!= STANDARDS_SEARCH_TAB && typeof (searchResultData) != "undefined" && searchResultData !== '') {
      searchResultData = JSON.parse(atob(searchResultData));
      //console.log("searchResultData", searchResultData);
      if (searchType === COLLECTIONS_SEARCH_TAB) {
          searchStore.setCollectionSearchResultData(searchResultData);
      } else if (searchType === UPDATES_SEARCH_TAB) {
          searchStore.setUpdatesSearchResultData(searchResultData);
      }
      searchStore['activeTab'] = searchType;
      searchStore['searchResultActive'] = true;
      searchStore['displaySearchBar'] = true;
    } else {
      await searchBasedOnContext(searchbarVars);
    }
  }
})
</script>

<template>
  <div class="container">
    <div class="universal-search">
      <div class="search-bar-wrapper" v-show="searchStore['displaySearchBar'] && !searchStore['searchResultActive']">
        <div class="search-bar search-bar-normal">
          <div :class="['search-bar-container', 'search-bar--normal']">
            <SearchTabs></SearchTabs>
            <StandardsSearchBar v-show="getItem('activeTab') === STANDARDS_SEARCH_TAB"></StandardsSearchBar>
            <CollectionsSearchBar v-show="getItem('activeTab') === COLLECTIONS_SEARCH_TAB"></CollectionsSearchBar>
            <UpdatesSearchBar v-show="getItem('activeTab') === UPDATES_SEARCH_TAB"></UpdatesSearchBar>
          </div>
        </div>
        <RecentlyViewed :docs="searchStore['recentlyViewedDocs']" v-show="getItem('activeTab') !== UPDATES_SEARCH_TAB"></RecentlyViewed>
      </div>

      <div class="search-bar search-bar-mobile" v-show="searchStore['displaySearchBar']">
        <div :class="['search-bar-container', 'search-bar--mobile']">
          <SearchTabs></SearchTabs>
          <StandardsSearchBar v-show="getItem('activeTab') === STANDARDS_SEARCH_TAB" :showFilters="false" :forMobile="true"></StandardsSearchBar>
          <CollectionsSearchBar v-show="getItem('activeTab') === COLLECTIONS_SEARCH_TAB" :showFilters="false" :forMobile="true"></CollectionsSearchBar>
          <UpdatesSearchBar v-show="getItem('activeTab') === UPDATES_SEARCH_TAB" :showFilters="!searchStore['searchResultActive']" :forMobile="true"></UpdatesSearchBar>
      </div>
      </div>
      <div class="search-bar search-bar-with-results" v-show="searchStore['displaySearchBar'] && searchStore['searchResultActive']">
        <div :class="['search-bar-container', 'search-bar-container--with-results']">
          <div class="search-bar--left">
            <SearchTabs></SearchTabs>
          </div>
          <div class="search-bar--right">
            <div class="search-bar--right-container">
                <StandardsSearchBar v-show="getItem('activeTab') === STANDARDS_SEARCH_TAB" :showFilters="false"></StandardsSearchBar>
                <CollectionsSearchBar v-show="getItem('activeTab') === COLLECTIONS_SEARCH_TAB" :showFilters="false"></CollectionsSearchBar>
                <UpdatesSearchBar v-show="getItem('activeTab') === UPDATES_SEARCH_TAB" :showFilters="false"></UpdatesSearchBar>
                <RecentlyViewed :docs="searchStore['recentlyViewedDocs']" class="recently-viewed-with--result" v-show="getItem('activeTab') !== UPDATES_SEARCH_TAB"></RecentlyViewed>
            </div>
          </div>
        </div>
        <RecentlyViewed :docs="searchStore['recentlyViewedDocs']" class="recently-viewed-with--result tablet" v-show="getItem('activeTab') !== UPDATES_SEARCH_TAB"></RecentlyViewed>
      </div>
      <SearchResult v-if="getItem('searchResultActive')"></SearchResult>
    </div>
  </div>
</template>
<style lang="scss">
    @use './scss/style.scss';
</style>
